import { kebabCase } from 'lodash'

import { findThumbnail } from '@marketplace-web/shared/utils'
import {
  TransactionDto,
  TransactionSide,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { transformCurrencyAmountDtoOrString } from '@marketplace-web/domain/currency-data'
import { FaqEntriesResp } from 'types/api/response/faq-entries-resp'
import { GetTransactionResp } from 'types/api/response/get-transaction-resp'
import { RecentTransactionsResp } from 'types/api/response/recent-transactions-resp'
import { FaqEntryDto } from 'types/dtos/faq-entry'
import { HelpCenterFaqEntryRubyDto } from 'types/dtos/help-center'
import { FaqEntryModel } from 'types/models/faq-entry'
import {
  HelpCenterFaqEntryRubyModel,
  HelpCenterHomeFaqEntryModel,
  HelpCenterNavigationItemModel,
  HelpCenterNavigationModel,
  HelpCenterQuestionModel,
  HelpCenterTransactionModel,
} from 'types/models/help-center'
import { MEMBER_PROFILE_URL } from 'constants/routes'

const transformHelpCenterHomeTransaction = (
  transaction: TransactionDto,
): HelpCenterTransactionModel => {
  const item = transaction.order.items.find(({ id }) => id === transaction.item_id)
  const oppositeUser =
    transaction.user_side === TransactionSide.Seller ? transaction.buyer : transaction.seller

  return {
    id: transaction.id,
    price: transformCurrencyAmountDtoOrString(transaction.offer.price, transaction.offer.currency),
    statusTitle: transaction.status_title,
    itemTitle: transaction.item_title,
    itemPhoto: item?.photos[0]?.url,
    itemPath: item?.url,
    bundleCount: transaction.items_count > 1 ? transaction.items_count : null,
    oppositeUserUsername: oppositeUser?.login,
    oppositeUserPhoto: findThumbnail(oppositeUser?.photo),
    oppositeUserPath: oppositeUser?.id ? MEMBER_PROFILE_URL(oppositeUser?.id) : undefined,
  }
}

export const transformHelpCenterTransactionsResponse = (
  response: RecentTransactionsResp,
): { transactions: Array<HelpCenterTransactionModel>; totalCount: number } => ({
  transactions: response.recent_transactions.map(transformHelpCenterHomeTransaction),
  totalCount: response.total_count,
})

export const transformHelpCenterTransactionResponse = ({ transaction }: GetTransactionResp) =>
  transformHelpCenterHomeTransaction(transaction)

const transformHomeFaqEntryDto = (faq: Omit<FaqEntryDto, 'body'>): HelpCenterHomeFaqEntryModel => ({
  id: faq.id,
  title: faq.title,
  icon: faq.icon,
})

export const transformHelpCenterHomeFaqsResponse = (response: FaqEntriesResp) =>
  response.faq_entries.map(transformHomeFaqEntryDto)

const transformHelpCenterNavigationDto = ({
  id,
  title,
  children,
}: Pick<FaqEntryDto, 'id' | 'title' | 'children'>): HelpCenterNavigationItemModel => ({
  id,
  title: title || null,
  childFaqEntries: children?.map(childFaqEntry => ({
    ...transformHelpCenterNavigationDto(childFaqEntry),
    faqEntryParentId: id,
  })),
})

export const transformHelpCenterNavigationResponse = ({
  faq_entries_parent_id,
  faq_entries_parent_title,
  faq_entries,
}: FaqEntriesResp): HelpCenterNavigationModel => ({
  rootFaqEntryId: faq_entries_parent_id,
  rootFaqEntryTitle: faq_entries_parent_title,
  childFaqEntries: faq_entries.map(transformHelpCenterNavigationDto),
})

export const transformHelpCenterFaqEntryRubyDto = (
  faqEntry: HelpCenterFaqEntryRubyDto,
): HelpCenterFaqEntryRubyModel => ({
  id: faqEntry.id,
  title: faqEntry.title,
  code: faqEntry.code,
  proceedButton: faqEntry.proceed_button,
  isContactAllowed: faqEntry.contact_allowed,
  isAnonymousAllowed: faqEntry.anonymous_allowed,
  isTransactionRequired: faqEntry.transaction_required,
  isComplaintsFaq: faqEntry.delays_ticket,
})

export const transformHelpCenterFaqEntryChildren = (
  { children }: FaqEntryModel,
  accessChannel: string,
  faqEntryParentId?: number | null,
  transactionId?: string,
): Array<HelpCenterQuestionModel> | null => {
  if (!children?.length) return null
  const pathPrefix = faqEntryParentId ? `/help/${faqEntryParentId}` : '/help'

  return children.map(({ id, title }) => {
    const mainPath = `${pathPrefix}/${kebabCase(`${id}-${title}`)}?access_channel=${accessChannel}`
    const path = transactionId ? `${mainPath}&transaction_id=${transactionId}` : mainPath

    return { id, title, path }
  })
}
